// Import FeatureIndex as a module with side effects to ensure
// it's registered as a serializable class on the main thread
import './feature_index';

import type {CollisionBoxArray} from './array_types';
import type Style from '../style/style';
import type StyleLayer from '../style/style_layer';
import type {TypedStyleLayer} from '../style/style_layer/typed_style_layer';
import type FeatureIndex from './feature_index';
import type Context from '../gl/context';
import type {FeatureStates} from '../source/source_state';
import type {SpritePositions} from '../util/image';
import type LineAtlas from '../render/line_atlas';
import type {CanonicalTileID, UnwrappedTileID} from '../source/tile_id';
import type {TileTransform} from '../geo/projection/tile_transform';
import type Point from '@mapbox/point-geometry';
import type {ProjectionSpecification} from '../style-spec/types';
import type {VectorTileFeature, VectorTileLayer} from '@mapbox/vector-tile';
import type {TileFootprint} from '../../3d-style/util/conflation';
import type {LUT} from "../util/lut";
import type {ImageVariant} from '../style-spec/expression/types/image_variant';
import type {ElevationFeature} from '../../3d-style/elevation/elevation_feature';
import type {ElevationParams} from '../source/elevation_coverage_snapshot';
import type {ImageId, StringifiedImageId} from '../style-spec/expression/types/image_id';
import type {StyleModelMap} from '../style/style_mode';
import type {GlobalProperties} from '../style-spec/expression';
import type ImageManager from '../render/image_manager';

export type BucketParameters<Layer extends StyleLayer> = {
    index: number;
    layers: Array<Layer>;
    zoom: number;
    lut: LUT | null;
    canonical: CanonicalTileID;
    pixelRatio: number;
    overscaling: number;
    collisionBoxArray: CollisionBoxArray;
    sourceLayerIndex: number;
    sourceLayerName?: string;
    sourceID: string;
    projection: ProjectionSpecification;
    tessellationStep: number | null | undefined;
    styleDefinedModelURLs: StyleModelMap;
    worldview: string | undefined;
    localizable: boolean;
    availableImages: ImageId[];
    maxUniformBufferBindings?: number | null;
    maxUniformBlockSizeDwords?: number | null;
};

export type ImageDependenciesMap = Map<StringifiedImageId, Array<ImageVariant>>;

export type GlyphDependencies = Record<string, Record<number, boolean>>;

export type PopulateParameters = {
    featureIndex: FeatureIndex;
    iconDependencies: ImageDependenciesMap;
    patternDependencies: ImageDependenciesMap;
    glyphDependencies: GlyphDependencies;
    availableImages: ImageId[];
    lineAtlas: LineAtlas;
    brightness: number | null | undefined;
    scaleFactor: number;
    showElevationIdDebug: boolean;
    elevationFeatures: ElevationFeature[] | undefined;
    /// Cross-source elevation registry (line and symbol layers).
    elevationParams: ElevationParams | null | undefined;
    /// True when cross-source elevation is active for this style.
    crossSourceElevationEnabled: boolean;
    // True when terrain is enabled — HD road-markup lines drape flat.
    terrainEnabled: boolean;
    activeFloors: Set<string> | undefined;
};

export type IndexedFeature = {
    feature: VectorTileFeature;
    id: number | string | undefined;
    index: number;
    sourceLayerIndex: number;
};

export type BucketFeature = {
    index: number;
    sourceLayerIndex: number;
    geometry: Array<Array<Point>>;
    properties: Record<PropertyKey, unknown>;
    type: 0 | 1 | 2 | 3;
    id?: string | number | null;
    // Array<[primaryId, secondaryIs]>
    readonly patterns: Record<string, string[]>;
    sortKey?: number;
};

export type AppearanceUpdateResult = {
    hasLayoutChanges: boolean,
    hasUboChanges: boolean
};

/**
 * The `Bucket` interface is the single point of knowledge about turning vector
 * tiles into WebGL buffers.
 *
 * `Bucket` is an abstract interface. An implementation exists for each style layer type.
 * Create a bucket via the `StyleLayer#createBucket` method.
 *
 * The concrete bucket types, using layout options from the style layer,
 * transform feature geometries into vertex and index data for use by the
 * vertex shader.  They also (via `ProgramConfiguration`) use feature
 * properties and the zoom level to populate the attributes needed for
 * data-driven styling.
 *
 * Buckets are designed to be built on a worker thread and then serialized and
 * transferred back to the main thread for rendering.  On the worker side, a
 * bucket's vertex, index, and attribute data is stored in `bucket.arrays:
 * ArrayGroup`.  When a bucket's data is serialized and sent back to the main
 * thread, is gets deserialized (using `new Bucket(serializedBucketData)`, with
 * the array data now stored in `bucket.buffers: BufferGroup`.  BufferGroups
 * hold the same data as ArrayGroups, but are tuned for consumption by WebGL.
 *
 * @private
 */
export interface Bucket {
    layerIds: Array<string>;
    hasPattern: boolean;
    layers: TypedStyleLayer[];
    stateDependentLayers: Array<TypedStyleLayer>;
    hasAppearances: boolean | null;
    readonly stateDependentLayerIds: Array<string>;
    readonly worldview: string | undefined;
    evaluateQueryRenderedFeaturePadding?: () => number;
    populate: (
        features: Array<IndexedFeature>,
        options: PopulateParameters,
        canonical: CanonicalTileID,
        tileTransform: TileTransform,
    ) => void;
    update: (
        states: FeatureStates,
        vtLayer: VectorTileLayer,
        availableImages: ImageId[],
        imagePositions: SpritePositions,
        layers: ReadonlyArray<TypedStyleLayer>,
        isBrightnessChanged: boolean,
        brightness?: number | null,
        canonical?: CanonicalTileID
    ) => void;
    isEmpty: () => boolean;
    upload: (context: Context, canonical?: CanonicalTileID, featureState?: FeatureStates, availableImages?: Array<ImageId>, globalProperties?: GlobalProperties) => void;
    uploadPending: () => boolean;
    /**
     * Release the WebGL resources associated with the buffers. Note that because
     * buckets are shared between layers having the same layout properties, they
     * must be destroyed in groups (all buckets for a tile, or all symbol buckets).
     *
     * @private
     */
    destroy: (reload?: boolean) => void;
    updateFootprints: (id: UnwrappedTileID, footprints: Array<TileFootprint>) => void;
    updateAppearances: (canonical?: CanonicalTileID, featureState?: FeatureStates, availableImages?: Array<ImageId>, globalProperties?: GlobalProperties, imageManager?: ImageManager, featureStateChanged?: boolean) => AppearanceUpdateResult;
    requiresStandardRuntime?: boolean;
    requiresHDRuntime?: boolean;
    // Called after deserialize on the main thread to reattach `expression` refs
    // on attribute binders (omitted from transfer — see `register()` calls in
    // program_configuration.ts). Each bucket iterates its own
    // ProgramConfigurationSet instances so nested locations (symbol text/icon,
    // fill elevatedStructures, fill-extrusion groundEffect) stay covered.
    // Buckets without binders (ClipBucket, model buckets) don't need it.
    updateExpressions?: (layers: ReadonlyArray<TypedStyleLayer>) => void;
    // Drops what new placement decided about this bucket's symbols, hiding all of them. Called when
    // the tile has been out of the render set, so placement has not seen it and its decisions have
    // gone stale (see SourceCache#_addTile's cache-return branch). Only SymbolBucket implements it.
    resetPlacementVisibility?: () => void;
}

export function deserialize(input: Array<Bucket>, style: Style): Record<string, Bucket> {
    const output: Record<string, Bucket> = {};

    // Guard against the case where the map's style has been set to null while
    // this bucket has been parsing.
    if (!style) return output;

    for (const bucket of input) {
        const layers = bucket.layerIds
            .map((id) => style.getLayer(id))
            .filter(Boolean);

        if (layers.length === 0) {
            continue;
        }

        // look up StyleLayer objects from layer ids (since we don't
        // want to waste time serializing/copying them from the worker)
        bucket.layers = layers;
        if (bucket.stateDependentLayerIds) {
            bucket.stateDependentLayers = bucket.stateDependentLayerIds.map((lId) => layers.filter((l) => l.id === lId)[0]);
        }
        if (bucket.updateExpressions) bucket.updateExpressions(layers);
        for (const layer of layers) {
            output[layer.fqid] = bucket;
        }
    }

    return output;
}
